import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { getItemConfig } from '../config/itemConfig';
import { useAuth, usePermissions, useAuthReady } from '../stores/auth';
import ItemCard from '../components/ItemCard';
import SEOManager from '../components/SEO/SEOManager';
import { useSEOData, SEOStats, SEODefinitions, SEOFAQSection } from '../components/SEO/SEOComponents';
import { useTracking } from '../hooks/useTracking';
import { useDeviceMode } from '../hooks/useDeviceMode';
import { useGlassmorphism } from '../hooks/useGlassmorphism';
import { ADMIN_LECCE_COORDS } from '../config/filters';
import { SORT_OPTIONS, calcDistance } from '../utils/itemListHelpers';
import { useItemListFilters } from '../hooks/useItemListFilters';
import ItemListFilters from '../components/ItemList/ItemListFilters';
import ItemListGrid from '../components/ItemList/ItemListGrid';
import ItemListPagination from '../components/ItemList/ItemListPagination';
import SidebarFilters from '../components/filters/SidebarFilters';
import { itemsApi } from '../utils/api';

interface ItemListProps {
  itemType: 'boats' | 'motors' | 'accessories';
}

export default function ItemList({ itemType }: ItemListProps) {
  const config = getItemConfig(itemType);
  const { user, isAuthenticated } = useAuth();
  const isAdmin = user?.is_admin === true;
  const { checkPermission } = usePermissions();
  const authReady = useAuthReady();
  
  const { isDesktop, isPWA } = useDeviceMode();
  const { glass, isGlassEnabled } = useGlassmorphism();
  const { trackPageView } = useTracking();
  const seoData = useSEOData();
  
  // Custom hook for filter state & logic
  const filterData = useItemListFilters({ itemType });
  const {
    quickSearch,
    activeFilters,
    activeLocation,
    sortBy,
    sortKey,
    setSortBy,
    page,
    setPage,
    itemsPerPage,
    setItemsPerPage,
    totalItems,
    setTotalItems,
    totalPages,
    setTotalPages,
    columns,
    setColumns,
    handleSearchChange,
    handleResetAll,
    handleSortChange,
    handlePriceFilterChange,
    setRadiusFromBadge,
    adminCoordsLoaded,
    showFilters,
    setShowFilters,
    hasActiveFilters,
    getActiveFiltersCount,
    ADMIN_LECCE_COORDS: HOOK_LECCE, // from hook (same)
    sidebarOpen,
    setSidebarOpen,
    filters,
    setFilters,
    location,
    setLocation,
    locationStatus,
    setLocationStatus,
    radius,
    setRadius,
    useLocation,
    setUseLocation,
    useFixedLocation,
    setUseFixedLocation,
    availableOnly,
    setAvailableOnly,
    statusFilter,
    setStatusFilter,
    debouncedSearch,
  } = filterData;
  
  // Main items query
  const { data: items = [], isLoading, isFetched, refetch } = useQuery(
    [itemType, activeFilters, sortKey, page, itemsPerPage],
    async () => {
      const wpLoggedIn = typeof window !== 'undefined' && (window as any).BOAT_TRADER_API?.is_logged_in === true;
      const hasToken = typeof window !== 'undefined' && !!(window as any).boat_trader_token;
      const hasUser = typeof window !== 'undefined' && !!(window as any).user;
      
      if (!wpLoggedIn && !hasToken && !hasUser) {
        // Public user
      } else if (!wpLoggedIn && !hasToken) {
        return [];
      }
      
      const apiMethod = isAuthenticated ? itemsApi.getItems : itemsApi.getItemsPublic;
      const response = await apiMethod(itemType, activeFilters);
      const responseData = response.data;
      
      if (!responseData || typeof responseData !== 'object') {
        console.warn('[ItemList] Invalid API response, returning empty array');
        return [];
      }
      
      // Parse pagination meta for both desktop and mobile
      if (responseData?.meta) {
        const meta = responseData.meta.pagination || responseData.meta || {};
        setTotalItems(meta.total || 0);
        setTotalPages(meta.total_pages || Math.ceil((meta.total || 0) / itemsPerPage));
      }
      
      if (responseData?.data?.items) return responseData.data.items;
      if (responseData?.data?.boats) return responseData.data.boats;
      if (responseData?.data?.motors) return responseData.data.motors;
      if (Array.isArray(responseData?.data)) return responseData.data;
      return [];
    },
    { 
      refetchOnWindowFocus: false,
      retry: 1,
      enabled: authReady && (!user?.is_admin || adminCoordsLoaded),
    }
  );
  
  // Mobile: accumulate items across pages (Load More)
  const [allItems, setAllItems] = useState<any[]>([]);
  const prevFiltersRef = useRef<string>('');
  
  // Reset accumulated items when filters change
  useEffect(() => {
    const filtersKey = JSON.stringify(activeFilters);
    if (prevFiltersRef.current && prevFiltersRef.current !== filtersKey) {
      setAllItems([]);
      if (!isDesktop) setPage(1);
    }
    prevFiltersRef.current = filtersKey;
  }, [activeFilters, isDesktop, setPage]);
  
  // Accumulate items on mobile
  useEffect(() => {
    if (isDesktop) return;
    if (page === 1) {
      setAllItems(items);
    } else {
      setAllItems(prev => {
        const existingIds = new Set(prev.map((i: any) => i.id));
        const newItems = items.filter((i: any) => !existingIds.has(i.id));
        return [...prev, ...newItems];
      });
    }
  }, [items, page, isDesktop]);
  
  // Items to display: accumulated on mobile, direct on desktop
  const displayItems = isDesktop ? items : allItems;
  
  const handleLoadMore = useCallback(() => {
    setPage(page + 1);
  }, [page, setPage]);
  
  // Fallback badges from backend config (render-safe, never undefined)
  const badgeStepsFromConfig = (window as any).BOAT_TRADER_API?.badge_steps;
  const configFallbackSteps = badgeStepsFromConfig && Array.isArray(badgeStepsFromConfig) && badgeStepsFromConfig.length > 0
    ? badgeStepsFromConfig
    : [];
  
  // Radius counts query — badge counts dinamici
  const { data: radiusCountsData, isFetched: radiusCountsFetched } = useQuery(
    [itemType, 'radiusCounts', activeLocation?.lat, activeLocation?.lng, statusFilter, availableOnly, debouncedSearch, filters.brand, filters.model, filters.min_price, filters.max_price],
    async () => {
      const lat = activeLocation?.lat ?? ADMIN_LECCE_COORDS.lat;
      const lng = activeLocation?.lng ?? ADMIN_LECCE_COORDS.lng;
      try {
        const response = await itemsApi.getRadiusCounts(itemType, {
          lat,
          lng,
          ...(statusFilter ? { status: statusFilter } : {}),
          ...(availableOnly ? { available_only: 'true' } : {}),
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
          ...(activeFilters?.brand ? { brand: activeFilters.brand } : {}),
          ...(activeFilters?.model ? { model: activeFilters.model } : {}),
          ...(activeFilters?.min_price ? { min_price: Number(activeFilters.min_price) } : {}),
          ...(activeFilters?.max_price ? { max_price: Number(activeFilters.max_price) } : {}),
        });
        return response.data;
      } catch (error) {
        console.error('[ItemList] Error fetching radius counts:', error);
        return null;
      }
    },
    { 
      enabled: itemType === 'boats' || itemType === 'motors' || itemType === 'accessories',
      staleTime: 120000,
    }
  );
  
  // Steps statici da config PHP, filtra quelli con count 0 quando caricati
  const dynamicRadiusList = useMemo(() => {
    if (radiusCountsData?.counts && radiusCountsFetched) {
      return configFallbackSteps.filter((step: number) => {
        return (radiusCountsData.counts[step] ?? 0) > 0;
      });
    }
    return configFallbackSteps;
  }, [configFallbackSteps, radiusCountsData, radiusCountsFetched]);
  
  const radiusBadgeCounts = useMemo(() => {
    if (radiusCountsData?.counts) {
      return radiusCountsData.counts;
    }
    return {};
  }, [radiusCountsData]);
  
  // Apply sorting
  const sortedItems = useMemo(() => {
    if (!items || items.length === 0) return [];
    
    if (sortBy === 'distance_asc' && activeLocation) {
      return [...items].sort((a, b) => {
        const itemLatA = a.lat || a.dealer_lat;
        const itemLngA = a.lng || a.dealer_lng;
        const itemLatB = b.lat || b.dealer_lat;
        const itemLngB = b.lng || b.dealer_lng;
        const distA = itemLatA && itemLngA
          ? calcDistance(activeLocation.lat, activeLocation.lng, itemLatA, itemLngA)
          : 999999;
        const distB = itemLatB && itemLngB
          ? calcDistance(activeLocation.lat, activeLocation.lng, itemLatB, itemLngB)
          : 999999;
        return distA - distB;
      });
    }
    
    return items;
  }, [items, sortBy, activeLocation]);
  
  // Track page view
  useEffect(() => {
    trackPageView(
      `/${itemType}`,
      window.location.href,
      'list',
      itemType === 'boats' ? 'boat' : 'motor'
    ).catch(console.error);
  }, [itemType, trackPageView]);
  
  return (
    <div className={isGlassEnabled ? glass.background : 'bg-white'} data-testid="item-list">
      <h1 className="sr-only">
        {itemType === 'boats'
          ? 'Barche usate e nuove in vendita'
          : itemType === 'motors'
            ? 'Motori marini in vendita'
            : 'Accessori nautici in vendita'}
      </h1>
      
      {isDesktop ? (
        <div className="flex-sticky-container">
          {/* Desktop Sidebar */}
          <div className={`w-64 flex-shrink-0 bg-white border-r border-gray-200 sticky-top h-screen overflow-y-auto ${sidebarOpen ? 'block' : 'hidden'}`}>
            <SidebarFilters
              data-testid="sidebar-filters"
              filters={filters}
              setFilters={setFilters}
              onPriceFilterChange={handlePriceFilterChange}
              location={location}
              setLocation={setLocation}
              locationStatus={locationStatus}
              setLocationStatus={setLocationStatus}
              radius={radius}
              setRadius={setRadius}
              useLocation={useLocation}
              setUseLocation={setUseLocation}
              useFixedLocation={useFixedLocation}
              setUseFixedLocation={setUseFixedLocation}
              availableOnly={availableOnly}
              setAvailableOnly={setAvailableOnly}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              selectedCity={null}
              setSelectedCity={() => {}}
              selectedProvince=""
              setSelectedProvince={() => {}}
              selectedRegion=""
              setSelectedRegion={() => {}}
            />
          </div>
          
          <div className="flex-1 min-w-0">
            <ItemListFilters
              isDesktop={isDesktop}
              itemType={itemType}
              config={config}
              filters={filters}
              setFilters={setFilters}
              location={location}
              setLocation={setLocation}
              locationStatus={locationStatus}
              setLocationStatus={setLocationStatus}
              radius={radius}
              setRadius={setRadius}
              useLocation={useLocation}
              setUseLocation={setUseLocation}
              useFixedLocation={useFixedLocation}
              setUseFixedLocation={setUseFixedLocation}
              availableOnly={availableOnly}
              setAvailableOnly={setAvailableOnly}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              showFilters={showFilters}
              setShowFilters={setShowFilters}
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
              activeLocation={activeLocation}
              hasActiveFilters={hasActiveFilters}
              getActiveFiltersCount={getActiveFiltersCount}
              radiusSteps={dynamicRadiusList}
              radiusBadgeCounts={radiusBadgeCounts}
              totalItems={totalItems}
              radiusCountsData={radiusCountsData}
              quickSearch={quickSearch}
              handleSearchChange={handleSearchChange}
              handleResetAll={handleResetAll}
              handlePriceFilterChange={handlePriceFilterChange}
              setRadiusFromBadge={setRadiusFromBadge}
              ADMIN_LECCE_COORDS={ADMIN_LECCE_COORDS}
            />
            <ItemListGrid
              isDesktop={isDesktop}
              items={sortedItems}
              isLoading={isLoading || !isFetched}
              refetch={refetch}
              itemType={itemType}
              config={config}
              columns={columns}
              setColumns={setColumns}
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              sortBy={sortBy}
              handleSortChange={handleSortChange}
              totalItems={totalItems}
              page={page}
              setPage={setPage}
              totalPages={totalPages}
              user={user}
              isAuthenticated={isAuthenticated}
              checkPermission={checkPermission}
              activeLocation={activeLocation}
              sortOptions={SORT_OPTIONS}
              onResetAll={handleResetAll}
              hasActiveFilters={hasActiveFilters}
            />
            <ItemListPagination
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </div>
        </div>
      ) : (
        <>
          {/* Mobile layout */}
          <ItemListFilters
            isDesktop={isDesktop}
            itemType={itemType}
            config={config}
            filters={filters}
            setFilters={setFilters}
            location={location}
            setLocation={setLocation}
            locationStatus={locationStatus}
            setLocationStatus={setLocationStatus}
            radius={radius}
            setRadius={setRadius}
            useLocation={useLocation}
            setUseLocation={setUseLocation}
            useFixedLocation={useFixedLocation}
            setUseFixedLocation={setUseFixedLocation}
            availableOnly={availableOnly}
            setAvailableOnly={setAvailableOnly}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            activeLocation={activeLocation}
            hasActiveFilters={hasActiveFilters}
            getActiveFiltersCount={getActiveFiltersCount}
            radiusSteps={dynamicRadiusList}
            radiusBadgeCounts={radiusBadgeCounts}
            totalItems={totalItems}
            radiusCountsData={radiusCountsData}
            quickSearch={quickSearch}
            handleSearchChange={handleSearchChange}
            handleResetAll={handleResetAll}
            handlePriceFilterChange={handlePriceFilterChange}
            setRadiusFromBadge={setRadiusFromBadge}
            ADMIN_LECCE_COORDS={ADMIN_LECCE_COORDS}
          />
          <ItemListGrid
            isDesktop={isDesktop}
            items={displayItems}
            isLoading={isLoading || !isFetched}
            refetch={refetch}
            itemType={itemType}
            config={config}
            columns={columns}
            setColumns={setColumns}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            sortBy={sortBy}
            handleSortChange={handleSortChange}
            totalItems={totalItems}
            page={page}
            setPage={setPage}
            totalPages={totalPages}
            user={user}
            isAuthenticated={isAuthenticated}
            checkPermission={checkPermission}
            activeLocation={activeLocation}
            sortOptions={SORT_OPTIONS}
            onResetAll={handleResetAll}
            hasActiveFilters={hasActiveFilters}
            onLoadMore={!isDesktop && page < totalPages ? handleLoadMore : undefined}
          />
        </>
      )}
      
      {/* SEO Manager */}
      {seoData && (
        <SEOManager
          title={seoData.title}
          description={seoData.description}
          image={seoData.image}
          url={window.location.href}
          type={seoData.type}
          stats={seoData.stats as SEOStats}
          definitions={seoData.definitions as SEODefinitions[]}
          faqSection={seoData.faqSection as SEOFAQSection}
        />
      )}
    </div>
  );
}
